import {
  Filter, Segment, SegmentRuleField,
} from "sharedTypes"

// BE's segment_rule.json schema rejects string `"5"` on these fields.
// Typed as `ReadonlySet<string>` so `Set.has()` accepts the widened
// `Filter.field: SegmentRuleField | string` without a TS error.
const NUMERIC_FIELDS: ReadonlySet<string> = new Set([
  SegmentRuleField.EmailsSent,
  SegmentRuleField.PlusOnesCount,
])

// Filters null/undefined defensively — legacy segments saved against an
// old strong-params bug had `value` entries dropped to null mid-array.
export const ruleValueToChipValues = (value: unknown): string[] => {
  if (value == null) return []
  if (Array.isArray(value)) {
    return value.filter((v) => v != null).map((v) => String(v))
  }

  return [String(value)]
}

export const chipToRule = ({ field, operator, values }: Filter) => {
  const coerce = (v: string): string | number => {
    if (!NUMERIC_FIELDS.has(field)) return v
    const n = parseInt(v, 10)

    return Number.isFinite(n) ? n : 0
  }
  const isArrayOp = operator === "in" || operator === "not_in"
    || operator === "has_all" || operator === "not_has_all"

  return {
    field,
    operator,
    value: isArrayOp ? values.map(coerce) : coerce(values[0] ?? ""),
  }
}

export const generateChipId = () => (
  `chip-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
)

// Empty values mean an in-progress edit or malformed URL — drop before
// sending so the wire doesn't carry `value: ""` / `value: 0` as a rule.
export const chipHasValue = (chip: Filter): boolean => (
  chip.values.length > 0 && chip.values.some((v) => v !== "")
)

const sameStringSet = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false
  const seen = new Set(a)

  return b.every((v) => seen.has(v))
}

export const chipsEqual = (a: Filter[], b: Filter[]): boolean => {
  if (a.length !== b.length) return false
  const byField = new Map(b.map((chip) => [chip.field, chip]))

  return a.every((chip) => {
    const counterpart = byField.get(chip.field)
    if (!counterpart) return false

    return chip.operator === counterpart.operator
      && sameStringSet(chip.values, counterpart.values)
  })
}

// AND is commutative on the BE, so rule order shouldn't drive the dirty
// flag — sort both sides into canonical strings and compare as multisets.
const ruleKey = (
  field: string, operator: string, values: string[],
): string => `${field}|${operator}|${[...values].sort().join(",")}`

const rulesEqual = (a: Segment["rules"], b: Filter[]): boolean => {
  if (a.length !== b.length) return false
  const aKeys = a.map((r) => {
    const values = Array.isArray(r.value)
      ? r.value.filter((v) => v != null).map(String)
      : [String(r.value)]

    return ruleKey(r.field, r.operator, values)
  }).sort()
  const bKeys = b.map((f) => ruleKey(f.field, f.operator, f.values)).sort()

  return aKeys.every((k, i) => k === bKeys[i])
}

// Empty filters means chips haven't been touched — hydrate from
// segment.rules directly rather than diff against zero.
export const isSegmentDirty = (
  segment: Segment | undefined,
  filters: Filter[],
): boolean => {
  if (!segment || filters.length === 0) return false

  return !rulesEqual(segment.rules, filters)
}

export const isSelectAllBlocked = (
  searchString: string,
  filters: Filter[],
  segment: Segment | undefined,
): boolean => (
  !!searchString || (filters.length > 0 && (!segment || isSegmentDirty(segment, filters)))
)
